import { useCallback, useState } from 'react'
import { isAxiosError } from 'axios'
import { bomCapturaApi, type UploadImagenArgs } from '@/services/api'
import { withNetworkRetry } from '@/utils/bomCaptura'
import type { ImagenUploadResponse } from '@/types/bomCaptura'

export type UploadStatus = 'idle' | 'uploading' | 'success' | 'error'

/** Error de red (sin respuesta del servidor) → reintentable. */
function esErrorDeRed(e: unknown): boolean {
  return isAxiosError(e) && !e.response
}

function mensajeError(e: unknown): string {
  if (isAxiosError(e)) {
    return (e.response?.data as { detail?: string } | undefined)?.detail ?? e.message
  }
  return e instanceof Error ? e.message : 'Error al subir la imagen'
}

const MAX_REINTENTOS = 2

/**
 * Subida de imagen a Power Automate (vía backend) con estado, progreso y
 * reintento automático ≤2 en errores de red. No bloquea la UI: la subida vive en
 * el hook y el usuario puede seguir navegando.
 */
export function useImageUpload(itemId: string, empresa: string, formula: string) {
  const [status, setStatus] = useState<UploadStatus>('idle')
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<ImagenUploadResponse | null>(null)

  const upload = useCallback(
    async (args: UploadImagenArgs): Promise<ImagenUploadResponse> => {
      setStatus('uploading')
      setProgress(0)
      setError(null)
      try {
        const res = await withNetworkRetry(
          () => bomCapturaApi.uploadImagen(itemId, empresa, formula, args, setProgress),
          esErrorDeRed,
          MAX_REINTENTOS,
        )
        setResult(res)
        setProgress(100)
        setStatus('success')
        return res
      } catch (e) {
        setStatus('error')
        setError(mensajeError(e))
        throw e
      }
    },
    [itemId, empresa, formula],
  )

  const reset = useCallback(() => {
    setStatus('idle')
    setProgress(0)
    setError(null)
    setResult(null)
  }, [])

  return { status, progress, error, result, upload, reset }
}
